import { useEffect } from "react";
import { useNavigate } from "react-router";
import { Tenant, useTenants } from "@/api/tenant.api";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { Routes } from "@/router/routes";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Building, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { languageService } from "@/services/language/language.service";

/**
 * Renders a list of tenants and allows the user to select one.
 */
export function TenantSelection() {
    const navigate = useNavigate();
    const { data: availableTenants, isLoading, error } = useTenants();
    const { t } = useTranslation();

    /**
     * Handles the selection of a tenant.
     * @param tenant The tenant to select.
     */
    const handleTenantSelect = (tenant: Tenant) => {
        useTenantStore.getState().selectTenant(tenant);
        navigate("/");
    };

    // If there are no tenants, navigate to the create tenant page
    useEffect(() => {
        if (availableTenants?.length === 0) {
            navigate(`/${Routes.CreateTenant}`);
        }
    }, [availableTenants, navigate]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="ml-2">Loading tenants...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <div className="text-red-500 mb-4">{error.message}</div>
                <Button variant="outline" onClick={() => navigate(`/${Routes.CreateTenant}`)}>
                    Create New Organization
                </Button>
            </div>
        );
    }

    return (
        <div className="container max-w-4xl mx-auto p-6 flex flex-col gap-6">
            <h1>
                {t("tenant.select.title")}
                {availableTenants?.length === 0 ? " (Keine Organisationen gefunden)" : ""}
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableTenants?.map((tenant) => (
                    <Card
                        key={tenant.id}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                        <CardHeader className="flex flex-col gap-2">
                            <CardTitle className="flex items-center gap-2">
                                <Building className="w-4 h-4 mr-2" />
                                {tenant.name}
                            </CardTitle>
                            <CardDescription>
                                {t("tenant.createdAt", {
                                    date: new Date(tenant.createdAt).toLocaleDateString(
                                        languageService.getCurrentLocale(),
                                        {
                                            year: "numeric",
                                            month: "long",
                                            day: "2-digit",
                                        },
                                    ),
                                })}
                            </CardDescription>
                        </CardHeader>
                        <CardFooter>
                            <Button onClick={() => handleTenantSelect(tenant)} className="w-full">
                                {t("general.select")}
                            </Button>
                        </CardFooter>
                    </Card>
                ))}
            </div>

            <Button
                variant="outline"
                onClick={() => navigate(`/${Routes.CreateTenant}`)}
                className="w-full"
            >
                {t("tenant.create.title")}
            </Button>
        </div>
    );
}
